/**
 * How a keybook key is revoked — ONE description per key type, read by every
 * screen that tells a person to revoke one (deleting a key, leaving an org, the
 * key's own panels). Deleting an entry removes only Papan's copy: whoever could
 * open it may hold the value, so what ends that is revoking it where it was issued.
 * Plan: docs/plans/2026-10-17_KEYBOOK_REVOKE.md.
 */

/** A provider Papan knows: its name and the page where its keys are managed. */
export interface KeybookProviderInfo {
  label: string
  /** Where a person creates, rotates and revokes this provider's keys. */
  manageUrl?: string
}

export const KEYBOOK_PROVIDERS: Readonly<Record<string, KeybookProviderInfo>> = {
  github: { label: 'GitHub', manageUrl: 'https://github.com/settings/tokens' },
  gitlab: { label: 'GitLab.com', manageUrl: 'https://gitlab.com/-/user_settings/personal_access_tokens' },
  slack: { label: 'Slack', manageUrl: 'https://api.slack.com/apps' },
  google: { label: 'Google', manageUrl: 'https://console.cloud.google.com/iam-admin/serviceaccounts' },
  openai: { label: 'OpenAI', manageUrl: 'https://platform.openai.com/api-keys' },
  stripe: { label: 'Stripe', manageUrl: 'https://dashboard.stripe.com/apikeys' },
  notion: { label: 'Notion', manageUrl: 'https://www.notion.so/my-integrations' },
  asana: { label: 'Asana', manageUrl: 'https://app.asana.com/0/my-apps' },
  vercel: { label: 'Vercel', manageUrl: 'https://vercel.com/account/tokens' },
  aws: { label: 'AWS' },
  postgres: { label: 'PostgreSQL' },
}

/** Where a GitHub user revokes an app's sign-in (a "Connect" key is one). */
export const GITHUB_AUTHORIZED_APPS_URL = 'https://github.com/settings/apps/authorizations'

export type KeybookRevocation =
  /** Papan can revoke exactly this key at the provider itself. No provider does
   *  this yet: a GitHub sign-in is the person's own, revoked by them (below). */
  | { mode: 'papan'; provider: string; providerLabel: string }
  /** A sign-in Papan obtained that only the person can revoke, at the provider. */
  | { mode: 'at-provider'; provider: string; providerLabel: string; url: string; kind: 'github-app' }
  /** A value someone pasted: Papan can't know who issued it — change it where it
   *  came from (the provider's page when Papan knows one). */
  | { mode: 'at-source'; provider?: string; providerLabel?: string; url?: string }

export function keybookRevocationFor(key: { credentialType?: string | null; provider?: string | null }): KeybookRevocation {
  const provider = key.provider ?? undefined
  const info = provider ? KEYBOOK_PROVIDERS[provider] : undefined
  if (key.credentialType === 'github') {
    return { mode: 'at-provider', provider: 'github', providerLabel: 'GitHub', url: GITHUB_AUTHORIZED_APPS_URL, kind: 'github-app' }
  }
  return info
    ? { mode: 'at-source', provider, providerLabel: info.label, ...(info.manageUrl ? { url: info.manageUrl } : {}) }
    : { mode: 'at-source' }
}
